import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ops, asList, fmtDate, OPS } from "@/admin/api";
import { PageTitle, EmptyState, Pill, ActionButton, Table, cell } from "@/admin/ui";

const TABS = [
  { key: "users", label: "Users" },
  { key: "reports", label: "Reports" },
  { key: "deletions", label: "Deletion requests" },
];

export default function Users() {
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState(null);
  const [reports, setReports] = useState(null);
  const [deletions, setDeletions] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [premiumDays, setPremiumDays] = useState("30");
  const [ridePassDays, setRidePassDays] = useState("3");

  useEffect(() => {
    ops(OPS.users).then((d) => setUsers(asList(d, "users"))).catch((e) => setError(e.message));
    ops(OPS.reports).then((d) => setReports(asList(d, "reports"))).catch(() => setReports([]));
    ops(OPS.deletions).then((d) => setDeletions(asList(d, "requests", "deletions"))).catch(() => setDeletions([]));
  }, []);

  const act = async (label, path, body) => {
    try {
      await ops(path, { method: "POST", body });
      toast.success(`${label} — done`);
      return true;
    } catch (e) {
      toast.error(`${label} failed: ${e.message}`);
      return false;
    }
  };

  const verifyEmail = async (e, u) => {
    e.stopPropagation();
    const id = u.id || u.uid || u.userId;
    const ok = await act("Email verified", OPS.verifyEmail(id));
    if (ok) {
      setUsers((list) => list.map((x) => (x === u ? { ...x, emailVerified: true } : x)));
    }
  };

  const statusOf = (u) => {
    if (u.presence?.is_online === true) return "online";
    if (u.presence?.is_online === false) return "offline";
    return u.online === true ? "online" : u.online === false ? "offline" : "unknown";
  };

  return (
    <div data-testid="admin-users-page">
      <PageTitle eyebrow="Admin Portal" title="User Management" />
      <div className="mb-6 flex gap-2" data-testid="users-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`rounded-lg border px-4 py-2 text-xs font-semibold transition-colors duration-200 ${
              tab === t.key ? "border-[#FF3333]/40 bg-[#FF3333]/10 text-white" : "border-white/10 text-neutral-400 hover:text-white"
            }`}
            data-testid={`users-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "users" && (
        <>
          {error ? (
            <EmptyState title="User list unavailable" hint={error} />
          ) : !users ? (
            <p className="label-tech text-neutral-500">Loading…</p>
          ) : !users.length ? (
            <EmptyState title="No users returned" hint="The backend responded but the list is empty." />
          ) : (
            <Table head={["User", "Status", "Tier", "Joined", "Last active", ""]} testId="users-table">
              {users.map((u) => {
                const id = u.id || u.uid || u.userId;
                const st = statusOf(u);
                return (
                  <tr key={id} className="cursor-pointer transition-colors duration-150 hover:bg-white/[0.03]" onClick={() => setSelected(u)} data-testid={`user-row-${id}`}>
                    <td className={cell}>
                      <p className="font-semibold text-white">{u.displayName || u.name || "—"}</p>
                      <p className="text-xs text-neutral-500">{u.email || id}</p>
                    </td>
                    <td className={cell}>
                      <Pill tone={st === "online" ? "green" : "neutral"}>{st}</Pill>
                    </td>
                    <td className={cell}>
                      <Pill tone={u.tier === "premium" || u.premium ? "amber" : "neutral"}>
                        {u.tier === "premium" || u.premium ? "Premium" : "Free"}
                      </Pill>
                    </td>
                    <td className={cell}>{fmtDate(u.createdAtMs || u.createdAt || u.joinedAt)}</td>
                    <td className={cell}>{fmtDate(u.presence?.last_seen || u.lastSignInMs || u.lastActiveAt || u.lastActive)}</td>
                    <td className={`${cell} text-right`}>
                      <span className="flex items-center justify-end gap-2">
                        {u.emailVerified ? (
                          <Pill tone="green">Verified</Pill>
                        ) : (
                          <ActionButton tone="cyan" onClick={(e) => verifyEmail(e, u)} data-testid={`verify-email-${id}`}>
                            Verify Email
                          </ActionButton>
                        )}
                        <span className="text-xs text-neutral-500">View →</span>
                      </span>
                    </td>
                  </tr>
                );
              })}
            </Table>
          )}
        </>
      )}

      {tab === "reports" && (
        !reports ? <p className="label-tech text-neutral-500">Loading…</p> :
        !reports.length ? <EmptyState title="No open reports" hint="Reported users and content will appear here." /> :
        <Table head={["Report", "Target", "Reason", "Submitted", "Actions"]} testId="reports-table">
          {reports.map((r, i) => (
            <tr key={r.id || i} data-testid={`report-row-${r.id || i}`}>
              <td className={cell}>{r.type || "Report"}</td>
              <td className={cell}>{r.targetName || r.targetId || "—"}</td>
              <td className={cell}>{r.reason || "—"}</td>
              <td className={cell}>{fmtDate(r.createdAt)}</td>
              <td className={`${cell} space-x-2`}>
                <ActionButton tone="cyan" onClick={() => act("Report resolved", OPS.resolveReport(r.id))} data-testid={`report-resolve-${r.id || i}`}>Resolve</ActionButton>
                <ActionButton onClick={() => act("Report dismissed", OPS.dismissReport(r.id))} data-testid={`report-dismiss-${r.id || i}`}>Dismiss</ActionButton>
              </td>
            </tr>
          ))}
        </Table>
      )}

      {tab === "deletions" && (
        !deletions ? <p className="label-tech text-neutral-500">Loading…</p> :
        !deletions.length ? <EmptyState title="No deletion requests" hint="Account deletion requests submitted by email or in-app will appear here." /> :
        <Table head={["Email", "Requested", "Status", "Note"]} testId="deletions-table">
          {deletions.map((d, i) => (
            <tr key={d.id || i} data-testid={`deletion-row-${d.id || i}`}>
              <td className={cell}>{d.email || "—"}</td>
              <td className={cell}>{fmtDate(d.createdAt || d.requestedAt)}</td>
              <td className={cell}><Pill tone={d.status === "completed" ? "green" : "amber"}>{d.status || "pending"}</Pill></td>
              <td className={cell}>{d.note || "—"}</td>
            </tr>
          ))}
        </Table>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/60" onClick={() => setSelected(null)} data-testid="user-detail-overlay">
          <div className="h-full w-full max-w-md overflow-y-auto border-l border-white/10 bg-[#0a0a0c] p-6" onClick={(e) => e.stopPropagation()} data-testid="user-detail-panel">
            <div className="flex items-start justify-between">
              <div>
                <p className="label-tech text-[10px] text-neutral-500">User profile</p>
                <h2 className="mt-1 font-display text-2xl font-bold text-white">{selected.displayName || selected.name || "—"}</h2>
                <p className="text-sm text-neutral-400">{selected.email}</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-neutral-500 hover:text-white" data-testid="user-detail-close">✕</button>
            </div>
            <dl className="mt-6 space-y-3 text-sm">
              {Object.entries(selected).slice(0, 14).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-white/5 pb-2">
                  <dt className="text-neutral-500">{k}</dt>
                  <dd className="text-right text-neutral-200">{typeof v === "object" ? JSON.stringify(v) : String(v ?? "—")}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-6 grid grid-cols-2 gap-2" data-testid="user-actions">
              <div className="col-span-2 flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  value={premiumDays}
                  onChange={(e) => setPremiumDays(e.target.value)}
                  className="h-9 w-24 rounded-lg border border-white/10 bg-white/5 px-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#FF3333]"
                  data-testid="premium-days-input"
                />
                <span className="text-xs text-neutral-500">days of Premium</span>
              </div>
              <ActionButton tone="cyan" onClick={() => act("Premium granted", OPS.grantPremium(selected.id || selected.uid), { days: Number(premiumDays) })} data-testid="action-grant-premium">Grant Premium</ActionButton>
              <ActionButton onClick={() => act("Premium revoked", OPS.revokePremium(selected.id || selected.uid))} data-testid="action-revoke-premium">Revoke Premium</ActionButton>
              <div className="col-span-2 mt-2 flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  value={ridePassDays}
                  onChange={(e) => setRidePassDays(e.target.value)}
                  className="h-9 w-24 rounded-lg border border-white/10 bg-white/5 px-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#FF3333]"
                  data-testid="ridepass-days-input"
                />
                <span className="text-xs text-neutral-500">days of Ride Pass</span>
              </div>
              <ActionButton tone="cyan" onClick={() => act("Ride Pass granted", OPS.grantRidePass(selected.id || selected.uid), { days: Number(ridePassDays) })} data-testid="action-grant-ridepass">Grant Ride Pass</ActionButton>
              <ActionButton onClick={() => act("Ride Pass revoked", OPS.revokeRidePass(selected.id || selected.uid))} data-testid="action-revoke-ridepass">Revoke Ride Pass</ActionButton>
              <ActionButton tone="red" onClick={() => act("User suspended", OPS.suspend(selected.id || selected.uid))} data-testid="action-suspend">Suspend</ActionButton>
              <ActionButton tone="cyan" onClick={() => act("User unsuspended", OPS.unsuspend(selected.id || selected.uid))} data-testid="action-unsuspend">Unsuspend</ActionButton>
              <ActionButton tone="red" onClick={() => act("User banned", OPS.ban(selected.id || selected.uid))} data-testid="action-ban">Ban</ActionButton>
              <ActionButton tone="cyan" onClick={() => act("User unbanned", OPS.unban(selected.id || selected.uid))} data-testid="action-unban">Unban</ActionButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
